import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { conversionFor } from '@core/constants/role-layout.constant';
import { slotSeriesValue } from '@core/helpers/slot-series.helper';
import {
  ChartComponent,
  XemexChartDataset,
} from '@custom-components/general/chart/chart.component';
import { ChartOptions, ScriptableContext, TooltipItem } from 'chart.js';
import { InsightSeries, displayValue } from '../installation-card-set';

// A series drawn beside the card's points that is not itself a point, such as the PV forecast.
// Its values are in `unit`, converted for display like a point's.
export interface ChartOverlay {
  label: string;
  unit: string;
  values: (number | null)[];
  color: string;
  borderDash: number[];
  // Shades the area between the line and zero.
  fill?: boolean;
}

interface Axis {
  id: string;
  unit: string;
}

// Chart.js gives a bar of value 0 no height, so a zero reads as missing data. Growing only those
// bars by this much on each side turns a zero into a thin line; a missing value still draws
// nothing, and a small value keeps its own height.
const ZERO_BAR_INFLATE = 1;

const BALANCE_DASH = [5, 4];
const BAR_STACK = 'bars';

@Component({
  selector: 'xemex-point-series-chart',
  standalone: true,
  imports: [ChartComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './point-series-chart.component.html',
})
export class PointSeriesChartComponent {
  series = input.required<InsightSeries[]>();
  labels = input.required<string[]>();
  loading = input<boolean>(false);
  overlays = input<ChartOverlay[]>([]);
  // Draws the series that are bars as lines. Overlays are lines either way.
  barsAsLines = input<boolean>(false);

  // The first axis a card meets is the primary one; a second axis, such as the heat transfer
  // rate beside a flow rate, is drawn on the right.
  private readonly axes = computed<Axis[]>(() => {
    const axes: Axis[] = [];
    for (const { layout, point } of this.series()) {
      if (!axes.some(axis => axis.unit === point.unit)) {
        axes.push({ id: axes.length === 0 ? 'y' : layout.axis, unit: point.unit });
      }
    }
    return axes;
  });

  readonly unit = computed(() => {
    const primary = this.axes()[0];
    return primary ? conversionFor(primary.unit).displayUnit : '';
  });

  // The value on screen is the value the API sent, converted for display. A `down` series is
  // negated for drawing only; the tooltip reads the unnegated value.
  readonly values = computed(() =>
    this.series().map(({ point, telemetry }) =>
      (telemetry?.slots ?? []).map(slot => displayValue(slotSeriesValue(point, slot), point.unit)),
    ),
  );

  private readonly overlayValues = computed(() =>
    this.overlays().map(overlay => overlay.values.map(value => displayValue(value, overlay.unit))),
  );

  readonly datasets = computed<XemexChartDataset[]>(() => {
    const datasets: XemexChartDataset[] = [
      ...this.pointDatasets(),
      ...this.overlays().map((overlay, index) => {
        const axisId = this.axes().find(axis => axis.unit === overlay.unit)?.id ?? 'y';
        return {
          label: overlay.label,
          data: this.overlayValues()[index],
          type: 'line' as const,
          color: overlay.color,
          fill: overlay.fill ?? false,
          borderDash: overlay.borderDash,
          ...(axisId === 'y' ? {} : { yAxisID: axisId }),
        };
      }),
    ];
    if (!datasets.some(dataset => dataset.stack === BAR_STACK)) return datasets;
    // A stacked scale also adds up lines that share a stack, so every other series gets its own.
    return datasets.map((dataset, index) =>
      dataset.stack === BAR_STACK ? dataset : { ...dataset, stack: `series-${index}` },
    );
  });

  // An up bar and a down bar, such as grid import and export, share one column per slot. Two bars
  // going the same way stay side by side, so neither hides the other.
  private readonly stackBars = computed(() => {
    if (this.barsAsLines()) return false;
    const bars = this.series().filter(({ layout }) => layout.chart === 'bar' && !layout.balance);
    const up = bars.filter(({ layout }) => layout.direction === 'up').length;
    const down = bars.length - up;
    return up === 1 && down === 1;
  });

  private readonly pointDatasets = computed<XemexChartDataset[]>(() =>
    this.series().map((series, index) => {
      const axisId = this.axes().find(axis => axis.unit === series.point.unit)?.id ?? 'y';
      const sign = series.layout.direction === 'down' ? -1 : 1;
      const balance = !!series.layout.balance;
      const stacked = this.stackBars() && series.layout.chart === 'bar' && !balance;
      return {
        label: series.point.name,
        data: this.values()[index].map(value => (value === null ? null : sign * value)),
        type: this.barsAsLines() || balance ? ('line' as const) : series.layout.chart,
        color: series.color,
        fill: false,
        ...(balance ? { borderDash: BALANCE_DASH } : {}),
        ...(stacked ? { stack: BAR_STACK } : {}),
        ...(axisId === 'y' ? {} : { yAxisID: axisId }),
      };
    }),
  );

  private readonly voltagePrimary = computed(() => this.series()[0]?.layout.axis === 'voltage');

  readonly options = computed<ChartOptions>(() => {
    const secondary = Object.fromEntries(
      this.axes()
        .slice(1)
        .map(axis => [
          axis.id,
          {
            position: 'right',
            grid: { display: false },
            ticks: {
              callback: (tick: unknown) =>
                `${tick} ${conversionFor(axis.unit).displayUnit}`.trimEnd(),
            },
          },
        ]),
    );
    return {
      spanGaps: false,
      elements: {
        bar: {
          // Chart.js resolves this once for an empty dataset too, with a context that has no
          // `parsed`, which is every chart before its telemetry has arrived.
          inflateAmount: (context: ScriptableContext<'bar'>) =>
            (context.parsed as { y: number } | undefined)?.y === 0 ? ZERO_BAR_INFLATE : 'auto',
        },
      },
      // The chart is created as a bar chart, whose value axis starts at zero; voltage swings a few
      // volts around its nominal value, so from zero its lines would lie flat.
      scales: { ...(this.voltagePrimary() ? { y: { beginAtZero: false } } : {}), ...secondary },
      plugins: {
        tooltip: {
          callbacks: {
            label: (item: TooltipItem<'line' | 'bar'>) => this.tooltipLabel(item),
          },
        },
      },
    } as ChartOptions;
  });

  private tooltipLabel(item: TooltipItem<'line' | 'bar'>): string {
    const pointCount = this.series().length;
    const isOverlay = item.datasetIndex >= pointCount;
    const unit = isOverlay
      ? this.overlays()[item.datasetIndex - pointCount]?.unit
      : this.series()[item.datasetIndex]?.point.unit;
    const value = isOverlay
      ? (this.overlayValues()[item.datasetIndex - pointCount]?.[item.dataIndex] ?? null)
      : (this.values()[item.datasetIndex]?.[item.dataIndex] ?? null);
    if (unit === undefined || value === null) {
      return `${item.dataset.label}: –`;
    }
    const { displayUnit, decimals } = conversionFor(unit);
    return `${item.dataset.label}: ${value.toFixed(decimals)} ${displayUnit}`.trimEnd();
  }
}
